import { useLayoutEffect } from 'react';

/**
 * Imperative SEO head manager.
 *
 * Manages: <title>, meta[name=description], meta[name=robots], link[rel=canonical],
 * OG (og:title, og:description, og:url, og:type, og:site_name, og:image, og:locale),
 * Twitter (twitter:card, twitter:title, twitter:description, twitter:image),
 * and one or more <script type="application/ld+json"> blocks.
 *
 * All tags managed by this component receive data-managed-by="seo-component" so subsequent
 * navigations can remove stale tags and re-render fresh ones. This works reliably across
 * SPA route changes — no react-helmet dependency, no invalid-child edge cases.
 */
const SITE_ORIGIN = 'https://www.moveracaremobility.com';

const buildBreadcrumbSchema = (crumbs) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((c, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: c.name,
    item: c.url.startsWith('http') ? c.url : `${SITE_ORIGIN}${c.url}`,
  })),
});

const MANAGED_ATTR = 'data-managed-by';
const MANAGED_VAL = 'seo-component';

function removeManagedTags() {
  document.head
    .querySelectorAll(`[${MANAGED_ATTR}="${MANAGED_VAL}"]`)
    .forEach((el) => el.remove());
}

function upsertMeta({ name, property, content }) {
  // Remove ALL existing meta tags with matching name/property (including static ones from index.html)
  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
  const el = document.createElement('meta');
  if (name) el.setAttribute('name', name);
  if (property) el.setAttribute('property', property);
  el.setAttribute('content', content);
  el.setAttribute(MANAGED_ATTR, MANAGED_VAL);
  document.head.appendChild(el);
}

function upsertLink({ rel, href }) {
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => {
    // Preserve non-managed links (icon, apple-touch-icon, preconnect, fonts)
    if (rel === 'canonical' || rel === 'alternate') el.remove();
  });
  const el = document.createElement('link');
  el.setAttribute('rel', rel);
  el.setAttribute('href', href);
  el.setAttribute(MANAGED_ATTR, MANAGED_VAL);
  document.head.appendChild(el);
}

function appendJsonLd(schemaObj) {
  const el = document.createElement('script');
  el.setAttribute('type', 'application/ld+json');
  el.setAttribute(MANAGED_ATTR, MANAGED_VAL);
  el.text = JSON.stringify(schemaObj);
  document.head.appendChild(el);
}

const Seo = ({ title, description, path = '/', image, noindex = false, schema, breadcrumbs }) => {
  const safeTitle = String(title || 'MoveraCare Mobility');
  const safeDescription = String(
    description || 'Non-emergency medical transportation across Greater Houston.'
  );
  const canonical = `${SITE_ORIGIN}${path}`;
  const ogImage = image
    ? image.startsWith('http') ? image : `${SITE_ORIGIN}${image}`
    : `${SITE_ORIGIN}/og-image.png`;
  const robots = noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large';

  useLayoutEffect(() => {
    document.title = safeTitle;
    removeManagedTags();

    upsertMeta({ name: 'description', content: safeDescription });
    upsertMeta({ name: 'robots', content: robots });
    upsertLink({ rel: 'canonical', href: canonical });

    upsertMeta({ property: 'og:type', content: 'website' });
    upsertMeta({ property: 'og:site_name', content: 'MoveraCare Mobility' });
    upsertMeta({ property: 'og:locale', content: 'en_US' });
    upsertMeta({ property: 'og:title', content: safeTitle });
    upsertMeta({ property: 'og:description', content: safeDescription });
    upsertMeta({ property: 'og:url', content: canonical });
    upsertMeta({ property: 'og:image', content: ogImage });
    upsertMeta({ property: 'og:image:secure_url', content: ogImage });
    upsertMeta({ property: 'og:image:type', content: 'image/png' });
    upsertMeta({ property: 'og:image:width', content: '1200' });
    upsertMeta({ property: 'og:image:height', content: '630' });
    upsertMeta({ property: 'og:image:alt', content: `${safeTitle} — MoveraCare Mobility social preview` });

    upsertMeta({ name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta({ name: 'twitter:title', content: safeTitle });
    upsertMeta({ name: 'twitter:description', content: safeDescription });
    upsertMeta({ name: 'twitter:image', content: ogImage });
    upsertMeta({ name: 'twitter:image:alt', content: `${safeTitle} — MoveraCare Mobility social preview` });

    const schemaArr = [];
    if (schema) {
      if (Array.isArray(schema)) schema.forEach((s) => s && schemaArr.push(s));
      else schemaArr.push(schema);
    }
    if (breadcrumbs && breadcrumbs.length) schemaArr.push(buildBreadcrumbSchema(breadcrumbs));
    schemaArr.forEach(appendJsonLd);

    // On unmount, clean up managed tags so the next route starts clean.
    return () => {
      removeManagedTags();
    };
  }, [safeTitle, safeDescription, canonical, ogImage, robots, JSON.stringify(schema), JSON.stringify(breadcrumbs)]);

  return null;
};

export default Seo;
